import { type ReactNode, useEffect, useState } from 'react';
import { uniq } from 'lodash';
import { Icon } from '@timeedit/ui-components';
import { Tree } from '@timeedit/ui-components/antd';
import DirectoryTree from '@timeedit/ui-components/antd/tree/DirectoryTree';
// COMPONENTS
import LoadingWrapper from '../../LoadingWrapper';

// TYPES
type Props = {
  data: any[];
  onSelect: any;
  autoExpandAllKeys?: boolean;
  showConnectorLine?: boolean;
  draggable?: boolean;
  onDrop?: any;
  isLoading?: boolean;
  fieldNames?: { title: string; key: string; children: string };
  titleRender?: (nodeData: any) => ReactNode;
  directoryTree?: boolean;
};

const getAllKeysInTreeData = (treeData: any[], keyProp: string): string[] => {
  return treeData.reduce((allKeys, node) => {
    const childKeys = getAllKeysInTreeData(node.children || [], keyProp);
    return uniq([...allKeys, node[keyProp], ...childKeys]);
  }, []);
};

const TreeListView = ({
  data,
  onSelect = () => {},
  autoExpandAllKeys = true,
  showConnectorLine = true,
  draggable = false,
  onDrop = undefined,
  isLoading = false,
  fieldNames = {
    title: 'title',
    key: 'key',
    children: 'children',
  },
  titleRender = undefined,
  directoryTree = false,
}: Props) => {
  const [expandedKeys, setExpandedKeys] = useState<string[]>([]);
  useEffect(() => {
    if (data.length)
      setExpandedKeys(
        autoExpandAllKeys ? getAllKeysInTreeData(data, fieldNames.key) : []
      );
  }, [data]);

  const onExpand = (newExpandedKeys: any[]) => {
    setExpandedKeys(newExpandedKeys);
  };

  return (
    <LoadingWrapper isLoading={isLoading}>
      {!isLoading && !directoryTree && (
        <Tree
          showLine={showConnectorLine ? { showLeafIcon: false } : false}
          draggable={draggable}
          onDrop={draggable ? onDrop : undefined}
          switcherIcon={<Icon id="DownOutlined" alt="Down" />}
          onSelect={(selectedValue: any, info: any) =>
            onSelect(selectedValue[0], info)
          }
          expandedKeys={expandedKeys}
          onExpand={onExpand}
          treeData={data}
          fieldNames={fieldNames}
          style={{ marginTop: '12px' }}
          titleRender={titleRender || undefined}
        />
      )}

      {!isLoading && directoryTree && (
        <DirectoryTree
          showLine={showConnectorLine ? { showLeafIcon: false } : false}
          draggable={draggable}
          onDrop={draggable ? onDrop : undefined}
          switcherIcon={<Icon id="DownOutlined" alt="Down" />}
          onSelect={(selectedValue: any, info: any) =>
            onSelect(selectedValue[0], info)
          }
          expandedKeys={expandedKeys}
          onExpand={onExpand}
          treeData={data}
          fieldNames={fieldNames}
          style={{ marginTop: '12px' }}
          titleRender={titleRender || undefined}
          defaultExpandParent
          showIcon={false}
        />
      )}
    </LoadingWrapper>
  );
};

export default TreeListView;
